/* istanbul ignore file */

import { ErrorPage, PageLoading, useRequestInit, withErrorBoundary, useFeature } from "adviesbox-shared";
import React, { ReactElement, useState, useEffect } from "react";
import useAbortableFetch from "use-abortable-fetch";
import { RapportageOutput, StandaardRapportOptions } from "../.generated/instellingen-forms/instellingen-formstypes";
import { saveData } from "../shared/utils/save-data";
import { mapRapportageSamenstellenDlTargetToUiField } from "./infra/map-rapportage-samenstellen-dl-target-to-ui-field";
import { mapRapportageSamenstellenDlToUi } from "./infra/map-rapportage-samenstellen-dl-to-ui";
import { mapRapportageSamenstellenUiToDl } from "./infra/map-rapportage-samenstellen-ui-to-dl";
import { RapportageSamenstellen } from "./rapportage-samenstellen";

// Guard function to detect if data is v1 or v2
const isDataV1 = (data: RapportageOutput): boolean => {
  if (!data?.rapportage?.structuren) return false;
  
  // Check if any structure has kolomselectie (v1 always has this filled)
  // and doesn't have standaardRapport as Hypotheekadvies (v2 indicator)
  return data.rapportage.structuren.some(structuur => 
    structuur.kolomselectie != null && 
    structuur.standaardRapport !== StandaardRapportOptions.Hypotheekadvies
  );
};

const isDataV2 = (data: RapportageOutput): boolean => {
  if (!data?.rapportage?.structuren) return false;
  
  // Check if all structures have kolomselectie as null (v2 indicator)
  return data.rapportage.structuren.every(structuur => 
    structuur.kolomselectie == null
  );
};

const RapportageSamenstellenAjaxComponent = (): ReactElement => {
  const { settings, user, params, requestInit } = useRequestInit();
  const url = `${settings.instellingenFormsOrigin}/Rapportage`;
  const { loading, error, data } = useAbortableFetch<RapportageOutput>(url, requestInit);

  const featureRapport2 = useFeature("FeatureRapport2");
  const [showTransitionLoading, setShowTransitionLoading] = useState(false);

  // Handle version mismatches between feature flag and data
  useEffect(() => {
    if (data && typeof data !== "string") {
      // Case 1: featureRapport2 is enabled but data is v1
      // Case 2: featureRapport2 is disabled but data is v2
      const needsReload = (featureRapport2 && isDataV1(data)) || (!featureRapport2 && isDataV2(data));
      
      if (needsReload) {
        setShowTransitionLoading(true);
        
        // Wait 5 seconds then reload the page
        const timer = setTimeout(() => {
          window.location.reload();
        }, 5000);

        return () => clearTimeout(timer);
      }
    }
  }, [featureRapport2, data]);

  if (!user) {
    return <ErrorPage error={new Error("Missing AuthContext")} />;
  }

  if (error) {
    return <ErrorPage error={error} data={data} />;
  }

  if (typeof data === "string") {
    return <ErrorPage error={new Error("Invalid data")} data={data} />;
  }

  if (loading || !data || showTransitionLoading) {
    return <PageLoading />;
  }

  const mappedProps = mapRapportageSamenstellenDlToUi(data);
  if (!mappedProps) {
    return <PageLoading />;
  }

  return (
    <RapportageSamenstellen
      saveData={saveData(
        user,
        params.vestiging ?? "",
        url,
        settings.OcpApimSubscriptionKey,
        mapRapportageSamenstellenUiToDl(featureRapport2),
        mapRapportageSamenstellenDlTargetToUiField
      )}
      {...mappedProps}
    />
  );
};

export const RapportageSamenstellenAjax = withErrorBoundary(RapportageSamenstellenAjaxComponent);
/* istanbul ignore else */ if (process.env.NODE_ENV !== "production")
  RapportageSamenstellenAjax.displayName = "RapportageSamenstellenAjax";
